// src/components/digital_twin/twin_engine/ui/NetworkTab.js
// How the asset connects: drainage linked to a well (role, distance, fall, inspection, impaired),
// the network risk model row for this asset, and what the active simulation cascades downstream.
import React, { memo } from 'react';
import { Box, Button, Flex, HStack, Icon, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { FaProjectDiagram, FaCrosshairs, FaArrowDown, FaArrowUp, FaBrain } from 'react-icons/fa';
import { Empty, KV, LevelBadge, Section, Stat, fmtDate, useTwinTokens } from './ui';

const ROLE = { catch_basin: 'Catch basin', drainage_well: 'Drainage well', drainage: 'Drain' };
const INS = { overdue: 'critical', never_inspected: 'high', due_soon: 'watch', current: 'ok' };

function NetworkTab({ profile, sources, risk, downstreamCount, simulationMeta, onFocusAsset }) {
  const t = useTwinTokens();
  const well = sources.wellIntel?.data || null;
  const near = well?.drainage?.nearby || [];
  const hasAnything = near.length || risk || downstreamCount > 0 || profile.wellIntel;
  if (!hasAnything) {
    return <Empty icon={FaProjectDiagram} title="No network links recorded">This asset has no linked drainage, no network-risk score and no simulated downstream impact.</Empty>;
  }
  return (
    <VStack align="stretch" spacing={4}>
      {profile.wellIntel && (
        <Section title="Linked drainage" icon={FaProjectDiagram}
          right={well?.drainage ? <Text fontSize="11px" color={t.faint}>within {well.drainage.radius_m} m</Text> : null}>
          {!well ? <Text fontSize="13px" color={t.muted}>{sources.wellIntel?.status === 'error' ? `Well intelligence unavailable: ${sources.wellIntel.error}` : 'Loading well intelligence…'}</Text>
            : !near.length ? <Text fontSize="13px" color={t.muted}>{well.latitude == null ? 'The well has no GPS position, so drainage cannot be matched.' : 'No catch basin or drainage well is mapped near this well.'}</Text>
              : near.map((d) => (
                <Flex key={d.id} gap={3} py={2} borderBottomWidth="1px" borderColor={t.border} _last={{ borderBottomWidth: 0 }} align="center">
                  <Box w="6px" alignSelf="stretch" borderRadius="full" bg={d.impaired ? 'red.400' : 'teal.400'} flexShrink={0} />
                  <Box flex={1} minW={0}>
                    <HStack spacing={2}><Text fontSize="13px" fontWeight="700" noOfLines={1}>{d.name || d.id}</Text><LevelBadge level="info">{ROLE[d.role] || d.role}</LevelBadge></HStack>
                    <Text fontSize="12px" color={t.muted}>
                      {d.distance_m} m away
                      {d.elevation_drop_m != null && <> · <Icon as={d.elevation_drop_m >= 0 ? FaArrowDown : FaArrowUp} boxSize={2.5} /> {Math.abs(d.elevation_drop_m)} m {d.elevation_drop_m >= 0 ? 'below (run-off flows to it)' : 'above the well'}</>}
                    </Text>
                    <Text fontSize="12px" color={d.impaired ? 'red.400' : t.muted}>
                      {d.reason || 'clear'}{d.inspection?.status ? ` · inspection ${d.inspection.status.replace(/_/g, ' ')}` : ''}{d.inspection?.last_inspected_date ? ` (${fmtDate(d.inspection.last_inspected_date)})` : ''}
                    </Text>
                  </Box>
                  {d.inspection?.status && <LevelBadge level={INS[d.inspection.status] || 'info'}>{d.inspection.status.replace(/_/g, ' ')}</LevelBadge>}
                  {onFocusAsset && <Button size="xs" variant="ghost" leftIcon={<FaCrosshairs />} onClick={() => onFocusAsset(d.id)}>Show</Button>}
                </Flex>
              ))}
        </Section>
      )}

      {risk && (
        <Section title="Network risk model" icon={FaBrain} accent>
          <SimpleGrid columns={2} spacing={3} mb={2}>
            <Stat label="Risk score" value={risk.score ?? '—'} sub={risk.band || ''} />
            <Stat label="Drivers" value={(risk.drivers || []).length} />
          </SimpleGrid>
          {(risk.drivers || []).map((d, i) => <KV key={i} label={typeof d === 'string' ? d : d.label} value={typeof d === 'object' && d.points != null ? `${d.points} pts` : ''} />)}
          {(risk.actions || []).length > 0 && <Text fontSize="12px" color={t.muted} mt={2}>Suggested: {(risk.actions || []).map((a) => (typeof a === 'string' ? a : a.text)).join(' · ')}</Text>}
        </Section>
      )}

      {downstreamCount > 0 && (
        <Section title="Simulated cascade" icon={FaProjectDiagram}>
          <Text fontSize="13px">{downstreamCount} downstream asset{downstreamCount > 1 ? 's are' : ' is'} impacted through this {profile.label.toLowerCase()} in “{simulationMeta?.title || 'the active simulation'}”.</Text>
        </Section>
      )}
    </VStack>
  );
}

export default memo(NetworkTab);
